// ON-407: the confirmation email can't carry where a web signup started — the
// "Confirm signup" template is fixed as
//   {{ .SiteURL }}/auth/confirm?token_hash={{ .TokenHash }}&type=signup
// (no redirect_to). So the event travels in the new user's user_metadata as
// `pending_rsvp` instead: written by signUp, read by /auth/confirm after
// verifyOtp, then cleared (best-effort).
//
// Only data is stored, never a path or URL: /auth/confirm rebuilds the event
// URL itself from a validated UUID, so metadata can't redirect anywhere else.
//
// Read-only check of other readers of raw_user_meta_data (running-club-backend
// handle_new_user, verification and OAuth-welcome functions; stazon-app): none
// iterate the whole object, so the extra key is ignored. Nothing here is sent
// to PostHog (identify is called with the user id only).
//
// `lang` is the language the event page was shown in, so the runner comes back
// to it after confirming (the page otherwise falls back to the browser's
// language). Only an exact supported locale code is stored or read back.

import { isLang, type Lang } from './i18n'

export interface PendingRsvp {
  event_id: string
  ref?: string
  src?: string
  lang?: Lang
  /** ISO timestamp of the signup. */
  at: string
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
// ref is a UUID, src a short surface name; anything else is dropped.
const TAG = /^[A-Za-z0-9._~:-]{1,64}$/
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

function tag(v: unknown): string | undefined {
  return typeof v === 'string' && TAG.test(v) ? v : undefined
}

export function buildPendingRsvp(
  eventId: string,
  tags: { ref?: string; src?: string; lang?: string },
  now: Date = new Date(),
): PendingRsvp {
  const ref = tag(tags.ref)
  const src = tag(tags.src)
  const lang = isLang(tags.lang) ? tags.lang : undefined
  return {
    event_id: eventId,
    ...(ref ? { ref } : {}),
    ...(src ? { src } : {}),
    ...(lang ? { lang } : {}),
    at: now.toISOString(),
  }
}

export interface PendingRsvpTarget {
  /** `/share/event/<uuid>?rsvp=confirm[&ref=..&src=..&lang=..]`, built here. */
  path: string
  eventId: string
  ref?: string
  src?: string
}

/** The event page to send a just-confirmed web signup to, or null. Never throws. */
export function pendingRsvpTarget(
  userMetadata: Record<string, unknown> | null | undefined,
  now: Date = new Date(),
): PendingRsvpTarget | null {
  const raw = userMetadata?.pending_rsvp
  if (!raw || typeof raw !== 'object') return null
  const p = raw as Record<string, unknown>
  if (typeof p.event_id !== 'string' || !UUID.test(p.event_id)) return null
  if (typeof p.at !== 'string') return null
  const at = Date.parse(p.at)
  if (Number.isNaN(at)) return null
  const age = now.getTime() - at
  // A little clock skew is fine; a future-dated entry is not.
  if (age > MAX_AGE_MS || age < -60_000) return null

  const eventId = p.event_id.toLowerCase()
  const ref = tag(p.ref)
  const src = tag(p.src)
  const q = new URLSearchParams({ rsvp: 'confirm' })
  if (ref) q.set('ref', ref)
  if (src) q.set('src', src)
  if (isLang(p.lang)) q.set('lang', p.lang)
  return {
    path: `/share/event/${eventId}?${q.toString()}`,
    eventId,
    ...(ref ? { ref } : {}),
    ...(src ? { src } : {}),
  }
}
